import { useState, useEffect } from "react";
import { getAllEstudiantes } from "../../services/estudiante.service";
import { getAllIdiomasByIdEstudiante } from "../../services/idioma.service";
import { getAllRegistrosByIdEstudianteIdIdiomaEstado } from "../../services/registro.service";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ContenedorPageTemplate } from "../../components/templates/ContenedorPageTemplate";
import { Globe, BookOpen, Sparkles, ArrowRight, CheckCircle2, GraduationCap, BookMarked } from "lucide-react";
import StatsCard from "../../components/animations/StatsCard";
import TypewriterText from "../../components/animations/TypewriterText";
import RippleEffect from "../../components/animations/RippleEffect";
import Confetti from "../../components/animations/Confetti";
import SubtleFloatingParticles from "../../components/animations/SubtleFloatingParticles";

export const InicioEstudiante = () => {
  const [idiomasEstudiante, setIdiomasEstudiante] = useState(null);
  const [nombreEstudiante, setNombreEstudiante] = useState("");
  const [modulosCompletados, setModulosCompletados] = useState(0);
  const [prevModulosCompletados, setPrevModulosCompletados] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const userName = useSelector((store) => store.usuario.username);

  useEffect(() => {
    if (modulosCompletados > prevModulosCompletados && prevModulosCompletados > 0) {
      setShowConfetti(true);
    }
    setPrevModulosCompletados(modulosCompletados);
  }, [modulosCompletados, prevModulosCompletados]);

  useEffect(() => {
    const obtenerIdiomas = async () => {
      try {
        const userInfo = JSON.parse(localStorage.getItem("userInfoES") || "{}");
        const cacheKey = `estudianteIdByUser_${userName || userInfo?.username || ""}`;
        let estudianteId = JSON.parse(localStorage.getItem(cacheKey) || "null");

        let estudiante = null;
        if (!estudianteId) {
          const estudiantesData = await getAllEstudiantes();
          estudiante = estudiantesData.find((est) => est.usuario.username === userName);
          if (estudiante?.estudiante?.idEstudiante) {
            estudianteId = estudiante.estudiante.idEstudiante;
            localStorage.setItem(cacheKey, JSON.stringify(estudianteId));
          } else {
            console.log("Estudiante no encontrado");
            return;
          }
        } else {
          const estudiantesData = await getAllEstudiantes();
          estudiante = estudiantesData.find((est) => est.usuario.username === userName);
        }
        
        if (estudiante?.persona) {
          const nombre = estudiante.persona.nombre || "";
          const apellido = estudiante.persona.apellido || "";
          setNombreEstudiante(`${nombre} ${apellido}`.trim());
        }

        const TTL_MS = 5 * 60 * 1000;
        const idiomasCacheKey = `idiomasByEstudiante_${estudianteId}`;
        const cached = JSON.parse(localStorage.getItem(idiomasCacheKey) || "null");
        const now = Date.now();

        if (cached?.data && typeof cached.timestamp === "number") {
          setIdiomasEstudiante(cached.data);
        }

        let idiomasFinal = null;
        if (!cached?.timestamp || now - cached.timestamp > TTL_MS) {
          const idiomasFresh = await getAllIdiomasByIdEstudiante(estudianteId);
          idiomasFinal = idiomasFresh;
          setIdiomasEstudiante(idiomasFresh);
          localStorage.setItem(
            idiomasCacheKey,
            JSON.stringify({ data: idiomasFresh, timestamp: now })
          );
        } else if (!cached?.data) {
          const idiomas = await getAllIdiomasByIdEstudiante(estudianteId);
          idiomasFinal = idiomas;
          setIdiomasEstudiante(idiomas);
          localStorage.setItem(
            idiomasCacheKey,
            JSON.stringify({ data: idiomas, timestamp: now })
          );
        } else {
          idiomasFinal = cached.data;
        }

        if (idiomasFinal && idiomasFinal.length > 0) {
          try {
            let totalCompletados = 0;
            const estadosCompletados = ["Aprobado", "Culminado"];
            
            for (const idioma of idiomasFinal) {
              for (const estado of estadosCompletados) {
                try {
                  const registros = await getAllRegistrosByIdEstudianteIdIdiomaEstado(
                    estudianteId,
                    idioma.idIdioma,
                    estado
                  );
                  if (registros && Array.isArray(registros) && registros.length > 0) {
                    console.log(`Módulos ${estado} en ${idioma.nombre}:`, registros.length);
                    totalCompletados += registros.length;
                  }
                } catch (err) {
                  if (err?.response?.status !== 404) {
                    console.log(`Error obteniendo registros para ${idioma.nombre} - ${estado}:`, err);
                  }
                }
              }
            }
            
            console.log("Total módulos completados:", totalCompletados);
            setModulosCompletados(totalCompletados);
          } catch (err) {
            console.log("Error obteniendo módulos completados:", err);
          }
        }
      } catch (err) {
        console.log(err);
      }
    };

    if (userName) {
      obtenerIdiomas();
    }
  }, [userName]);

  return (
    <ContenedorPageTemplate fondo={true} objetos={idiomasEstudiante}>
      {/* Confetti effect */}
      <Confetti trigger={showConfetti} onComplete={() => setShowConfetti(false)} />
      
      {/* Partículas sutiles de fondo */}
      <div className="absolute inset-0 pointer-events-none z-0">
        <SubtleFloatingParticles count={60} />
      </div>

      {/* Hero Section compacto y animado */}
      <div className="relative overflow-hidden mb-6 rounded-2xl bg-white/50 backdrop-blur-sm py-6 md:py-8 border border-white/40 shadow-lg z-10">
        <div className="relative max-w-2xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-theme-yellow animate-bounce" style={{ animationDelay: '0s' }} />
            <h1 className="text-xl md:text-2xl font-bold">
              <span className="bg-gradient-to-r from-theme-blue to-blue-700 bg-clip-text text-transparent">
                Bienvenido a la Plataforma Educativa
              </span>
            </h1>
            <Sparkles className="w-4 h-4 text-theme-yellow animate-bounce" style={{ animationDelay: '0.3s' }} />
          </div>
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <span className="text-xl md:text-2xl font-black text-theme-blue">Edu</span>
            <span className="text-xl md:text-2xl font-black text-theme-yellow animate-pulse">Server</span>
          </div>
          {nombreEstudiante && (
            <p className="text-lg md:text-xl font-semibold text-theme-blue mb-2">
              <TypewriterText 
                text={nombreEstudiante} 
                speed={80} 
                delay={600}
                className="bg-gradient-to-r from-theme-blue to-blue-700 bg-clip-text text-transparent"
              />
            </p>
          )}
          <p className="text-gray-500 text-xs md:text-sm">Explora tus idiomas y comienza tu aprendizaje</p>
        </div>
      </div>

      {/* Sección de idiomas con libros decorativos de fondo */}
      <div className="relative flex flex-col items-center px-4 py-8 z-10">
        {/* Libros decorativos de fondo */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10">
          <BookMarked className="absolute top-4 left-8 w-16 h-16 text-theme-blue transform -rotate-12 animate-float" />
          <BookMarked className="absolute top-12 right-12 w-12 h-12 text-theme-yellow transform rotate-12 animate-float" style={{ animationDelay: '0.5s' }} />
          <BookMarked className="absolute bottom-8 left-16 w-14 h-14 text-theme-blue transform rotate-6 animate-float" style={{ animationDelay: '1s' }} />
          <BookMarked className="absolute bottom-4 right-20 w-20 h-20 text-theme-yellow transform -rotate-6 animate-float" style={{ animationDelay: '1.5s' }} />
          <BookOpen className="absolute top-1/2 left-1/4 w-10 h-10 text-theme-blue transform -rotate-45 animate-float" style={{ animationDelay: '0.8s' }} />
          <BookOpen className="absolute top-1/3 right-1/3 w-12 h-12 text-theme-yellow transform rotate-45 animate-float" style={{ animationDelay: '1.2s' }} />
        </div>

        <div className="relative flex flex-col items-center w-full">
          <div className="flex items-center gap-3 mb-8">
            <Globe className="w-6 h-6 md:w-8 md:h-8 text-theme-blue animate-spin-slow" />
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-theme-blue">
              Mis Idiomas
            </h2>
            <Globe className="w-6 h-6 md:w-8 md:h-8 text-theme-yellow animate-spin-slow" style={{ animationDirection: 'reverse' }} />
          </div>
          {idiomasEstudiante && idiomasEstudiante.length > 0 ? (
            <IdiomasList idiomas={idiomasEstudiante} />
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">Aún no tienes idiomas asignados</p>
            </div>
          )}
        </div>
      </div>
    </ContenedorPageTemplate>
  );
};

const getLanguageIcon = (nombre) => {
  const nombreLower = nombre?.toLowerCase() || '';
  if (nombreLower.includes('francés') || nombreLower.includes('french')) return '🇫🇷';
  if (nombreLower.includes('inglés') || nombreLower.includes('english')) return 'EN';
  if (nombreLower.includes('español') || nombreLower.includes('spanish')) return '🇪🇸';
  if (nombreLower.includes('portugués') || nombreLower.includes('portuguese')) return '🇵🇹';
  if (nombreLower.includes('alemán') || nombreLower.includes('german')) return '🇩🇪';
  if (nombreLower.includes('italiano') || nombreLower.includes('italian')) return '🇮🇹';
  if (nombreLower.includes('chino') || nombreLower.includes('chinese')) return 'CN';
  if (nombreLower.includes('japonés') || nombreLower.includes('japanese')) return '🇯🇵';
  return '🌐';
};

const getLanguageCode = (nombre, sigla) => {
  const nombreLower = nombre?.toLowerCase() || '';
  const customCodes = {
    'inglés': 'EN',
    'english': 'EN',
    'chino mandarín': 'ZH',
    'chino': 'ZH',
    'chinese': 'ZH',
    'francés': 'FR',
    'french': 'FR',
    'alemán': 'DE',
    'german': 'DE',
    'portugués': 'PT',
    'portuguese': 'PT',
    'italiano': 'IT',
    'italian': 'IT',
    'español': 'ES',
    'spanish': 'ES',
    'japonés': 'JA',
    'japanese': 'JA',
  };

  if (nombreLower && customCodes[nombreLower]) {
    return customCodes[nombreLower];
  }

  if (sigla && typeof sigla === 'string') {
    const cleanedSigla = sigla.trim();
    if (cleanedSigla.length <= 3) {
      return cleanedSigla.toUpperCase();
    }
  }

  if (nombre && typeof nombre === 'string') {
    const normalized = nombre.replace(/[^a-zA-Záéíóúñü]/g, ' ').trim();
    const words = normalized.split(/\s+/);
    if (words.length === 1 && words[0].length >= 2) {
      return words[0].slice(0, 2).toUpperCase();
    }
    if (words.length >= 2) {
      return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase();
    }
  }

  return 'IDI';
};

const IdiomasList = ({ idiomas }) => (
  <>
    <style>{`
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes spin-slow {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(360deg);
        }
      }
      @keyframes float {
        0%, 100% {
          transform: translateY(0px) rotate(var(--rotate-start, 0deg));
        }
        50% {
          transform: translateY(-15px) rotate(var(--rotate-end, 0deg));
        }
      }
      .animate-spin-slow {
        animation: spin-slow 8s linear infinite;
      }
      .animate-float {
        animation: float 3s ease-in-out infinite;
      }
      .fade-in-up {
        animation: fadeInUp 0.6s ease-out both;
      }
      .idiomas-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
        gap: 1.5rem;
        justify-items: center;
      }
      @media (max-width: 640px) {
        .idiomas-grid {
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }
      }
      @media (min-width: 641px) and (max-width: 1024px) {
        .idiomas-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
      }
      @media (min-width: 1025px) and (max-width: 1280px) {
        .idiomas-grid {
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
        }
      }
      @media (min-width: 1281px) {
        .idiomas-grid {
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }
      }
    `}</style>
    <ul className="idiomas-grid max-w-[1400px] mx-auto w-full">
      {idiomas.map((idioma, index) => (
        <li
          key={index}
          className="group relative fade-in-up"
          style={{
            animationDelay: `${index * 0.1}s`
          }}
        >
          <Link 
            to={`/modulos/${idioma?.idIdioma}`}
            className="block h-full"
          >
            <RippleEffect className="block h-full">
              <div
                className="relative h-full bg-white/60 backdrop-blur-md rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-white/50 hover:border-theme-blue/50 overflow-hidden z-20"
                style={{ minHeight: '260px', minWidth: '240px', maxWidth: '360px', margin: '0 auto' }}
              >
              {/* Gradiente de fondo animado */}
              <div className="absolute inset-0 bg-gradient-to-br from-theme-blue/5 via-transparent to-theme-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              {/* Indicador de visto */}
              <div className="absolute top-2 right-2 z-20">
                <CheckCircle2 className="w-5 h-5 text-green-500 fill-green-500/20 stroke-2 animate-pulse" />
              </div>
              
              {/* Contenido */}
              <div className="relative p-4 flex flex-col items-center justify-center h-full">
                {/* Icono del idioma */}
                <div className="text-4xl md:text-5xl mb-3 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  {getLanguageIcon(idioma?.nombre)}
                </div>
                
                {/* Nombre y sigla */}
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-2 text-center capitalize group-hover:text-theme-blue transition-colors duration-300">
                  {idioma?.nombre}
                </h3>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-theme-blue to-blue-600 text-white rounded-full font-semibold text-xs md:text-sm group-hover:from-blue-700 group-hover:to-blue-800 transition-all duration-300 shadow-md group-hover:shadow-lg">
                  <span>{getLanguageCode(idioma?.nombre, idioma?.sigla)}</span>
                </div>
                
                {/* Flecha animada */}
                <div className="flex items-center gap-1.5 text-theme-blue opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-2">
                  <span className="text-xs md:text-sm font-semibold">Explorar módulos</span>
                  <ArrowRight className="w-3 h-3 md:w-4 md:h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>

              {/* Efecto de brillo al hacer hover */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            </RippleEffect>
          </Link>
        </li>
      ))}
    </ul>
  </>
);
export default InicioEstudiante;
